import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Menu, Search, Trophy, Newspaper, Radio, CalendarDays, Users, Building2,
  ShieldCheck, ShoppingBag, Sparkles, Crown, Flag, User, LogOut, Settings,
  Bell, Plus, ChevronDown, Inbox, Wand2, Clock, HandCoins, Gavel, BookOpen,
  HelpCircle, Mail, HeartPulse, Store, Globe,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import api from "@/lib/api";
import { NotificationBell } from "@/components/NotificationBell";

// V12.x — Menu simplifié :
// • Calendrier retiré (peu utilisé, remplaçable par filtres actualités).
// • Mémoire du sport promue en 1er niveau (toujours visible).
// • Aide en ligne uniquement pour les visiteurs non connectés (déplacée dans "+" sinon).
// • Nouveau lien Contact dans "+".
const MAIN_LINKS_BASE = [
  { to: "/", label: "Accueil", icon: Sparkles },
  { to: "/actualites", label: "Actualités", icon: Newspaper },
  { to: "/athletes", label: "Athlètes", icon: Users },
  { annuaire: true, label: "Annuaire", icon: Users },
  { to: "/soutien", label: "Soutien aux sportifs", icon: HandCoins },
  { to: "/road-2027", label: "Jeux du Pacifique 2027", icon: Flag },
  { to: "/memoire", label: "Mémoire du sport", icon: BookOpen },
  // --- Menu "+ Plus" (dropdown) : entrées secondaires ---
  { to: "/contact", label: "Contact", icon: Mail },
];

function useMainLinks(user) {
  // Aide en ligne : visible en primaire pour les visiteurs, sinon dans "+".
  // Contact : toujours dans "+" pour tout le monde.
  if (user) {
    return [
      ...MAIN_LINKS_BASE.slice(0, 7),   // 7 premiers primaires (jusqu'à road-2027)
      { to: "/contact", label: "Contact", icon: Mail },
      { to: "/aide", label: "Aide en ligne", icon: HelpCircle },
    ];
  }
  return [
    ...MAIN_LINKS_BASE.slice(0, 7),
    { to: "/aide", label: "Aide en ligne", icon: HelpCircle },
    { to: "/contact", label: "Contact", icon: Mail },
  ];
}

// V9.21 — Sous-entrées de l'annuaire (Personnes / Structures / Partenaires)
// V13.21 — Ajout Athlètes du Pacifique (population documentaire, hors AITO)
const ANNUAIRE_SUBLINKS = {
  Personnes: [
    { to: "/athletes", label: "Athlètes", icon: Users },
    { to: "/coachs", label: "Coachs & Encadrants", icon: ShieldCheck },
    { to: "/annuaire/athletes-du-pacifique", label: "Athlètes du Pacifique", icon: Globe },
  ],
  Structures: [
    { to: "/clubs", label: "Clubs", icon: Building2 },
    { to: "/federations", label: "Fédérations", icon: Flag },
  ],
  Partenaires: [
    { to: "/annuaire/sante-performance", label: "Santé & Performance", icon: HeartPulse },
    { to: "/annuaire/boutiques-services", label: "Boutiques & Services", icon: Store },
  ],
};

// Index séparateur : tout au-dessus = visible, tout en dessous = menu "+ Plus"
const NAV_VISIBLE_COUNT = 7;

function Brand({ compact = false }) {
  // V8 : logo plus visible — desktop 72px / mobile 56px
  return (
    <Link
      to="/"
      data-testid="header-brand-link"
      aria-label="Retour à l'accueil AITO.PF"
      title="Accueil AITO.PF"
      className="flex items-center gap-3 group transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98]"
    >
      <img
        src="/branding/aito-logo.png"
        alt="AITO.PF — Pour les sportifs"
        className={compact
          ? "h-12 sm:h-14 w-auto object-contain drop-shadow-sm"
          : "h-[56px] sm:h-[64px] md:h-[72px] w-auto object-contain drop-shadow-sm"}
      />
    </Link>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  const [pendingCount, setPendingCount] = useState(0);
  const [adminPending, setAdminPending] = useState(0);
  const [copfPending, setCopfPending] = useState(0);
  const [myFedsCount, setMyFedsCount] = useState(0); // V13.16 — hub /mes-federations

  const roles = user?.roles || [];
  const isAdmin = roles.includes("admin");
  const isCopf = roles.includes("copf");
  const isFederation = roles.includes("federation");

  useEffect(() => {
    if (!user) {
      setPendingCount(0); setAdminPending(0); setCopfPending(0); setMyFedsCount(0);
      return;
    }
    let cancelled = false;
    const fetchCounts = async () => {
      // 1) Validations personnelles (entity-links)
      try {
        const { data } = await api.get("/entity-links/inbox/count?status=pending");
        if (!cancelled) setPendingCount(data?.count || 0);
      } catch { /* silencieux */ }
      // 2) Compteurs Admin (à traiter global)
      if (isAdmin) {
        try {
          const { data } = await api.get("/admin/stats/pending");
          if (!cancelled) setAdminPending(data?.total || 0);
        } catch { /* silencieux */ }
      }
      // 3) Compteurs COPF (à traiter global)
      if (isCopf) {
        try {
          const { data } = await api.get("/copf/stats/pending");
          if (!cancelled) setCopfPending(data?.total || 0);
        } catch { /* silencieux */ }
      }
      // 4) V13.16 — Fédés gérées (hub /mes-federations) : léger, tolérant à l'erreur
      try {
        const { data } = await api.get("/federations/mine");
        if (!cancelled) setMyFedsCount(data?.count || 0);
      } catch { /* silencieux */ }
    };
    fetchCounts();
    const t = setInterval(fetchCounts, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user, isAdmin, isCopf]);

  if (!user) {
    return (
      <div className="flex items-center gap-2">
        <Link to="/connexion" data-testid="header-login-button">
          <Button variant="outline" size="sm" className="h-11 min-w-[44px] border-[hsl(var(--aito-lagoon))] text-[hsl(var(--aito-lagoon))] hover:bg-[hsl(var(--aito-lagoon))] hover:text-black">
            Connexion
          </Button>
        </Link>
        <Link to="/connexion?next=/creer-ma-fiche" data-testid="header-create-card-button">
          <Button size="sm" className="h-11 min-w-[44px] bg-[hsl(var(--aito-lagoon))] hover:bg-[hsl(var(--aito-lagoon-2))] text-black">
            <Plus className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Créer ma fiche</span>
          </Button>
        </Link>
      </div>
    );
  }

  const initials = (user.name || user.email).split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

  const Item = ({ to, icon: Icon, label, badge, testid, onClick }) => {
    const inner = (
      <span className="flex items-center justify-between w-full">
        <span className="flex items-center">
          <Icon className="h-4 w-4 mr-2 text-muted-foreground" /> {label}
        </span>
        {badge > 0 && (
          <span className="ml-2 text-[10px] font-bold bg-[hsl(var(--aito-lagoon))] text-black rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </span>
    );
    if (onClick) {
      return (
        <DropdownMenuItem onClick={onClick} data-testid={testid}>{inner}</DropdownMenuItem>
      );
    }
    return (
      <DropdownMenuItem asChild>
        <Link to={to} data-testid={testid}>{inner}</Link>
      </DropdownMenuItem>
    );
  };

  return (
    <div className="flex items-center gap-1.5">
      <NotificationBell />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {/* V9.21 — Sprint UX Simplification : plus de badge rouge cumulé sur l'avatar.
              Les compteurs sont portés par la cloche (notifications) + entrées dashboard
              (Validations / Espace Admin / Espace COPF) afin d'éviter les doublons. */}
          <button
            data-testid="header-user-menu"
            className="relative flex items-center gap-2 px-1 py-1 rounded-full hover:bg-muted focus-ring"
            aria-label={`Menu de ${user.name || user.email}`}
          >
            <Avatar className="h-8 w-8">
              <AvatarImage src={user.avatar_url} />
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <ChevronDown className="h-4 w-4 text-muted-foreground hidden sm:inline" />
          </button>
        </DropdownMenuTrigger>

        {/* V9.20 — Menu simplifié : ~9 entrées maximum, groupées, badges intelligents */}
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel className="truncate">
            {user.name || user.email}
            {(isAdmin || isCopf) && (
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-normal mt-0.5">
                {isAdmin && "Administrateur"}{isAdmin && isCopf && " · "}{isCopf && "COPF"}
              </div>
            )}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />

          <Item to="/dashboard" icon={User} label="Mon espace" testid="menu-dashboard" />

          <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground font-normal pt-2">Créer</DropdownMenuLabel>
          <Item to="/creer-ma-fiche" icon={Plus} label="Ma fiche" testid="menu-create" />
          <Item to="/publier-article" icon={Newspaper} label="Publier un article" testid="menu-publish-article" />

          <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground font-normal pt-2">Mes contenus</DropdownMenuLabel>
          <Item to="/mes-articles" icon={Newspaper} label="Mes articles" testid="menu-mine-articles" />
          {/* V11 Hotfix — Livret AITO masqué (redondant avec Fiche PDF). Route conservée. */}
          <Item to="/mes-structures" icon={Building2} label="Mes structures" testid="menu-my-structures" />

          {pendingCount > 0 && (
            <>
              <DropdownMenuSeparator />
              <Item to="/mes-validations" icon={Inbox} label="Validations" badge={pendingCount} testid="menu-validations" />
            </>
          )}

          {(isAdmin || isCopf || isFederation || myFedsCount > 0) && <DropdownMenuSeparator />}

          {/* V13.16 — Hub Fédération : visible si l'utilisateur gère au moins 1 fédé
              (owner_user_id OU EntityManager approved), OU s'il a le rôle federation.
              Source unique : /api/federations/mine (backend centralise via
              is_user_federation_manager). */}
          {(isFederation || myFedsCount > 0) && (
            <Item to="/mes-federations" icon={Crown} label="Mes fédérations" badge={myFedsCount > 1 ? myFedsCount : 0} testid="menu-my-federations" />
          )}

          {(isAdmin || isCopf) && (
            <Item to="/copf/validations" icon={Crown} label="Validations palmarès · COPF" testid="menu-copf-validations" />
          )}
          {(isAdmin || isFederation) && (
            <Item to="/federation/validations" icon={ShieldCheck} label="Validations palmarès · Fédération" testid="menu-fed-validations" />
          )}

          {isAdmin && (
            <Item to="/admin" icon={Settings} label="Espace Admin" badge={adminPending} testid="menu-admin" />
          )}
          {isCopf && (
            <Item to="/copf" icon={ShieldCheck} label="Espace COPF" badge={copfPending} testid="menu-copf-dashboard" />
          )}

          <DropdownMenuSeparator />
          <Item icon={LogOut} label="Déconnexion" testid="menu-logout" onClick={logout} />
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export default function MainLayout() {
  const loc = useLocation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  useEffect(() => { setOpen(false); window.scrollTo(0, 0); }, [loc.pathname]);

  // V12.x — Liens de navigation dynamiques (Aide déplacée selon rôle visiteur/connecté)
  const MAIN_LINKS = useMainLinks(user);

  // V11.7 — Fond d'écran bannière AITO.PF GLOBAL (toutes pages).
  // - Sur la Home au chargement (scrollY <= 120) : pleine couleur, immersif.
  // - Partout ailleurs / au scroll : filigrane visible (~35%).
  const isHome = loc.pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 120);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  // Opacité de la bannière selon la page + scroll
  const bannerOpacity = isHome && !scrolled ? 1 : 0.35;
  // Voile de lisibilité : 0 sur la Home au chargement, 0.55 sinon
  const veilOpacity = isHome && !scrolled ? 0 : 0.55;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* V11.8 — Filigrane bannière AITO.PF global, présent sur toutes les pages.
          Positionné SOUS le header (top-14 = 56px) pour ne pas rogner l'image
          par la barre de menu opaque. */}
      <div
        aria-hidden
        data-testid="global-banner-bg"
        className="fixed left-0 right-0 bottom-0 pointer-events-none bg-cover bg-center transition-opacity duration-700 ease-out"
        style={{
          top: "56px",
          backgroundImage: "url('/branding/hero-banniere.jpg')",
          opacity: bannerOpacity,
          zIndex: 0,
        }}
      />
      <div
        aria-hidden
        className="fixed left-0 right-0 bottom-0 pointer-events-none bg-background transition-opacity duration-700 ease-out"
        style={{ top: "56px", opacity: veilOpacity, zIndex: 0 }}
      />
      <header className="sticky top-0 z-50 border-b bg-background backdrop-blur-sm shadow-sm">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-14 items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="lg:hidden focus-ring h-11 w-11" data-testid="header-mobile-menu-button" aria-label="Menu">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-80 p-0 bg-[hsl(var(--aito-black))] text-white border-r-0">
                  <SheetTitle className="sr-only">Menu</SheetTitle>
                  <div className="p-5 border-b border-white/10">
                    <Brand />
                  </div>
                  <nav className="py-3">
                    {MAIN_LINKS.map((entry) => {
                      if (entry.annuaire) {
                        return (
                          <div key="annuaire" className="border-l-2 border-transparent">
                            <div className="px-5 py-3 text-sm font-medium text-white/85 flex items-center gap-3">
                              <entry.icon className="h-4 w-4" /> {entry.label}
                            </div>
                            {Object.entries(ANNUAIRE_SUBLINKS).map(([group, links]) => (
                              <div key={group}>
                                <div className="px-5 pt-2 text-[10px] uppercase tracking-wide text-white/40">{group}</div>
                                {links.map(({ to, label, icon: SubIcon }) => (
                                  <NavLink
                                    key={to}
                                    to={to}
                                    data-testid={`mobile-nav-${to.replace(/\//g, "") || "home"}`}
                                    className={({ isActive }) =>
                                      `flex items-center gap-3 px-8 py-2.5 text-sm border-l-2 ${
                                        isActive ? "border-[hsl(var(--aito-lagoon))] bg-white/5 text-[hsl(var(--aito-lagoon))]" : "border-transparent text-white/75 hover:bg-white/5"
                                      }`
                                    }
                                  >
                                    <SubIcon className="h-3.5 w-3.5" /> {label}
                                  </NavLink>
                                ))}
                              </div>
                            ))}
                          </div>
                        );
                      }
                      const { to, label, icon: Icon } = entry;
                      return (
                        <NavLink key={to} to={to} end={to === "/"} data-testid={`mobile-nav-${to.replace(/\//g, "") || "home"}`}
                          className={({ isActive }) =>
                            `flex items-center gap-3 px-5 py-3 text-sm font-medium border-l-2 ${
                              isActive ? "border-[hsl(var(--aito-lagoon))] bg-white/5 text-[hsl(var(--aito-lagoon))]" : "border-transparent text-white/85 hover:bg-white/5"
                            }`
                          }>
                          <Icon className="h-4 w-4" /> {label}
                        </NavLink>
                      );
                    })}
                  </nav>
                  {/* V9.9 — Bloc Connexion / Créer ma fiche visible si non connecté */}
                  {!user && (
                    <div className="px-5 pt-3 pb-5 border-t border-white/10 space-y-2">
                      <p className="text-[11px] text-white/60 uppercase tracking-wide">Rejoindre AITO.PF</p>
                      <Link to="/connexion" data-testid="mobile-menu-login" className="block">
                        <Button variant="outline" size="sm" className="w-full justify-start border-white/30 text-white hover:bg-white/10 hover:text-white">
                          <User className="h-4 w-4 mr-2" /> Se connecter / créer un compte
                        </Button>
                      </Link>
                      <Link to="/connexion?next=/creer-ma-fiche" data-testid="mobile-menu-create" className="block">
                        <Button size="sm" className="w-full justify-start bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]">
                          <Plus className="h-4 w-4 mr-2" /> Créer ma fiche sportive
                        </Button>
                      </Link>
                    </div>
                  )}
                  <div className="p-5 border-t border-white/10 text-xs text-white/60">
                    © AITO.PF — La mémoire vivante du sport polynésien.
                  </div>
                </SheetContent>
              </Sheet>
              <Brand />
            </div>

            <nav className="hidden lg:flex items-center gap-1 overflow-x-auto no-scrollbar">
              {MAIN_LINKS.slice(0, NAV_VISIBLE_COUNT).map((entry) => {
                if (entry.annuaire) {
                  const isAnnuaireActive = ["/athletes", "/coachs", "/clubs", "/federations"].some((p) => loc.pathname.startsWith(p));
                  return (
                    <DropdownMenu key="annuaire">
                      <DropdownMenuTrigger asChild>
                        <button
                          data-testid="nav-annuaire"
                          className={`px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap inline-flex items-center gap-1 ${
                            isAnnuaireActive ? "text-[hsl(var(--aito-lagoon))]" : "text-foreground/80 hover:text-foreground hover:bg-muted"
                          }`}
                        >
                          {entry.label}
                          <ChevronDown className="h-3.5 w-3.5" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-64">
                        {Object.entries(ANNUAIRE_SUBLINKS).map(([group, links], gi) => (
                          <div key={group}>
                            {gi > 0 && <DropdownMenuSeparator />}
                            <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground font-normal">{group}</DropdownMenuLabel>
                            {links.map(({ to, label, icon: SubIcon }) => (
                              <DropdownMenuItem key={to} asChild>
                                <Link to={to} data-testid={`nav-annuaire-${to.replace(/\//g, "")}`}>
                                  <SubIcon className="h-4 w-4 mr-2 text-muted-foreground" /> {label}
                                </Link>
                              </DropdownMenuItem>
                            ))}
                          </div>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  );
                }
                const { to, label } = entry;
                return (
                  <NavLink key={to} to={to} end={to === "/"} data-testid={`nav-${to.replace(/\//g, "") || "home"}`}
                    className={({ isActive }) =>
                      `px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap ${
                        isActive ? "text-[hsl(var(--aito-lagoon))]" : "text-foreground/80 hover:text-foreground hover:bg-muted"
                      }`
                    }>
                    {label}
                  </NavLink>
                );
              })}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" data-testid="nav-more" aria-label="Plus de pages" title="Plus de pages">
                    <Plus className="h-4 w-4" /><ChevronDown className="h-3.5 w-3.5 ml-0.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {MAIN_LINKS.slice(NAV_VISIBLE_COUNT).map(({ to, label }) => (
                    <DropdownMenuItem key={to} asChild><Link to={to} data-testid={`nav-more-${to.replace(/\//g, "") || "home"}`}>{label}</Link></DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>

            <div className="flex items-center gap-2">
              <Link to="/aide" data-testid="header-help-button" aria-label="Aide en ligne" title="Aide en ligne">
                <Button variant="ghost" size="icon" className="focus-ring"><HelpCircle className="h-4 w-4" /></Button>
              </Link>
              <Link to="/recherche" data-testid="header-search-button" aria-label="Rechercher">
                <Button variant="ghost" size="icon" className="focus-ring"><Search className="h-4 w-4" /></Button>
              </Link>
              <UserMenu />
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-x-clip relative" style={{ zIndex: 1 }} data-testid="app-main">
        <Outlet />
      </main>

      <div className="relative" style={{ zIndex: 1 }}>
      {/* V9.21 — Sprint UX Simplification : footer différencié.
          - Applicatif (Admin/COPF/Wizard/édition/dashboard) → footer léger.
          - Public → footer institutionnel complet. */}
      {(() => {
        const p = loc.pathname;
        const isAppShell =
          p.startsWith("/admin") ||
          p.startsWith("/copf") ||
          p.startsWith("/dashboard") ||
          p.startsWith("/creer-ma-fiche") ||
          /\/edit(\/|$|\?)/.test(p) ||
          /^\/athletes\/[^/]+\/edit/.test(p) ||
          p.startsWith("/contrib/");
        if (isAppShell) {
          return (
            <footer className="mt-8 border-t bg-background text-muted-foreground" data-testid="app-footer-slim">
              <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs">
                <span>© {new Date().getFullYear()} AITO.PF — La mémoire vivante du sport polynésien.</span>
                <div className="flex items-center gap-3 flex-wrap">
                  <Link to="/legal/mentions-legales" className="hover:text-foreground">Mentions légales</Link>
                  <span aria-hidden>·</span>
                  <Link to="/legal/cgu" className="hover:text-foreground">CGU</Link>
                  <span aria-hidden>·</span>
                  <Link to="/legal/confidentialite" className="hover:text-foreground">Confidentialité</Link>
                  <span aria-hidden>·</span>
                  <Link to="/legal/cookies" className="hover:text-foreground">Cookies</Link>
                  <span aria-hidden>·</span>
                  <Link to="/legal" className="hover:text-foreground font-medium">Toutes →</Link>
                </div>
              </div>
            </footer>
          );
        }
        return (
          <footer className="mt-12 border-t bg-[hsl(var(--aito-black))] text-white/80" data-testid="app-footer-public">
            <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-10 grid gap-8 md:grid-cols-4 text-sm">
              <div>
                <Brand />
                <p className="mt-3 text-white/60 max-w-xs">La mémoire vivante du sport polynésien. Un média, des profils et la route vers les Jeux du Pacifique 2027.</p>
              </div>
              <div>
                <h4 className="font-display text-white text-sm mb-3">Annuaire</h4>
                <ul className="space-y-2 text-white/70">
                  <li className="text-[10px] uppercase tracking-wide text-white/40">Personnes</li>
                  <li><Link to="/athletes" className="hover:text-white">Athlètes</Link></li>
                  <li><Link to="/annuaire/athletes-du-pacifique" className="hover:text-white">Athlètes du Pacifique</Link></li>
                  <li><Link to="/coachs" className="hover:text-white">Coachs & Encadrants</Link></li>
                  <li className="pt-2 text-[10px] uppercase tracking-wide text-white/40">Structures</li>
                  <li><Link to="/clubs" className="hover:text-white">Clubs</Link></li>
                  <li><Link to="/federations" className="hover:text-white">Fédérations</Link></li>
                  <li className="pt-2 text-[10px] uppercase tracking-wide text-white/40">Partenaires</li>
                  <li><Link to="/annuaire/sante-performance" className="hover:text-white">Santé & Performance</Link></li>
                  <li><Link to="/annuaire/boutiques-services" className="hover:text-white">Boutiques & Services</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="font-display text-white text-sm mb-3">Découvrir</h4>
                <ul className="space-y-2 text-white/70">
                  <li><Link to="/actualites" className="hover:text-white">Actualités</Link></li>
                  <li><Link to="/soutien" className="hover:text-white">Soutien aux sportifs</Link></li>
                  <li><Link to="/road-2027" className="hover:text-white">Jeux du Pacifique 2027</Link></li>
                  <li><Link to="/memoire" className="hover:text-white">Mémoire du sport polynésien</Link></li>
                  <li><Link to="/aide" className="hover:text-white">Aide en ligne</Link></li>
                  <li><Link to="/creer-ma-fiche" className="hover:text-white">Créer ma fiche</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="font-display text-white text-sm mb-3">Légal</h4>
                {/* V13.34 — Référentiel 2026-10-27 : 10 rubriques canoniques
                    normalisées (confidentialite, images, soutien), legacy
                    préservé via LEGACY_TO_CANONICAL côté API. */}
                <ul className="space-y-1.5 text-white/70 columns-1 sm:columns-2 sm:gap-6">
                  <li><Link to="/legal/mentions-legales" className="hover:text-white">Mentions légales</Link></li>
                  <li><Link to="/legal/cgu" className="hover:text-white">CGU</Link></li>
                  <li><Link to="/legal/confidentialite" className="hover:text-white">Confidentialité</Link></li>
                  <li><Link to="/legal/cookies" className="hover:text-white">Cookies</Link></li>
                  <li><Link to="/legal/contact-droits" className="hover:text-white">Contact et droits</Link></li>
                  <li><Link to="/legal/images" className="hover:text-white">Images et autorisations</Link></li>
                  <li><Link to="/legal/mineurs" className="hover:text-white">Mineurs</Link></li>
                  <li><Link to="/legal/ia" className="hover:text-white">Intelligence artificielle</Link></li>
                  <li><Link to="/legal/contributeurs" className="hover:text-white">Contributeurs</Link></li>
                  <li><Link to="/legal/soutien" className="hover:text-white">Soutien en préparation</Link></li>
                </ul>
                <Link
                  to="/legal"
                  className="mt-3 inline-block text-[11px] text-white/50 hover:text-white/80 underline underline-offset-2"
                  data-testid="footer-legal-index"
                >
                  Toutes les informations légales →
                </Link>
              </div>
            </div>
            <div className="border-t border-white/10">
              <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-4 text-xs text-white/50 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                <span>© {new Date().getFullYear()} AITO.PF — La mémoire vivante du sport polynésien.</span>
                <span className="font-mono">Jeux du Pacifique 2027</span>
              </div>
            </div>
          </footer>
        );
      })()}
      </div>

      <Toaster richColors position="top-right" />
    </div>
  );
}
